import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnInit,
    Output,
    Input,
} from "@angular/core";
import {
    FormArray,
    FormBuilder,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import { AccountService } from "../../../data-access-layer/account/account.service";
import { CreateAccountRequest } from "../../../model/account/account";
import { NotifService } from "../../../core/notification/notif.service";
import { UserStateService } from "../../../auth/user-state-service";
import { AuthService } from "../../../auth/auth.service";
import { OverlaysService } from "src/app/data-access-layer/global-overlays/overlays.service";
import { ActivatedRoute } from "@angular/router";
import { GroupService } from "src/app/data-access-layer/groups/group.service";
import { DatapointsAggregateService } from "src/app/data-access-layer/datapoints/datapoints-aggregate.service";
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { LayerExposedService } from "../services/layer-exposed.service";
import { DatasetFieldScope } from "src/app/model/dataset/dataset-field-scope";
import { decodeHtmlText } from "src/app/core/utils/util-master";
import { LayerExposedConstants } from '../layer-exposed.constants';

@Component({
    selector: "add-layer-exposed",
    templateUrl: "./add-layer-exposed.component.html",
    styleUrls: ["./add-layer-exposed.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddLayerExposedComponent implements OnInit {
    @Output() updatedSuccessfully = new EventEmitter();
    @Output() onCancelClose = new EventEmitter();
    @Input() existingData: any; 
    @Input() dataToEdit: any;
    @Input() isEdit: boolean;
    createAccountForm: UntypedFormGroup;
    datasetLinkMap = {};
    externalOverlays: any;
    locationDatasetDropdown: any; // Base Value
    policyDatasetDropdown: any; // Limit Value and Attachment Point (numeric only)
    policyDatasetAllFields: any; // All policy fields for Policy ID selection
    locationTIVFields: any; // Location fields with TIV in their name
    locationPolicyIdFields: any; // Location fields for Policy ID (STRING fields)
    locationClientFields: any; // Location text fields for Group by Client
    policyMinimumDeductibleFields: any; // Policy fields matching MINIMUM DEDUCTIBLE
    policyMaximumDeductibleFields: any; // Policy fields matching MAXIMUM DEDUCTIBLE
    policyIdFields: any; // Policy SHORT_TEXT fields for Policy ID breakdown

    accountId: number;

    locationDataset: any;
    policyDataset: any;

    isFormDisabled = false;
    constructor(
        private readonly accountService: AccountService,
        private readonly notifService: NotifService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly route: ActivatedRoute,
        private layerExposeService: LayerExposedService,
        private fb: FormBuilder,

    ) {
    }
    ngOnChanges() {
        if (!this.isEdit) {
            this.initForm();
            this.createAccountForm.controls.calculationType.setValue('');
            this.createAccountForm.controls.name.setValue('');
            this.createAccountForm.controls.policyIdPolicy.setValue('');
            this.createAccountForm.controls.client.setValue('');
            this.createAccountForm.controls.tivValue.setValue('');
            this.createAccountForm.controls.layerValue.setValue('');
            this.createAccountForm.controls.attachmentPoint.setValue('');
            this.createAccountForm.controls.participation.setValue('');
        }
        this.accountId = +this.route.snapshot.paramMap.get("accountId");
        this.initForm();
        if (this.existingData) {
            this.patchExistingData();
        }
    }

    ngOnInit() {
        if (!this.isEdit) {
            this.initForm();
            this.createAccountForm.reset();
            this.createAccountForm.controls.calculationType.setValue('');
            this.createAccountForm.controls.name.setValue('');
            this.createAccountForm.controls.policyIdPolicy.setValue('');
            this.createAccountForm.controls.client.setValue('');
            this.createAccountForm.controls.tivValue.setValue('');
            this.createAccountForm.controls.layerValue.setValue('');
            this.createAccountForm.controls.attachmentPoint.setValue('');
            this.createAccountForm.controls.participation.setValue('');
        }
        this.accountId = +this.route.snapshot.paramMap.get("accountId");
        this.getData();
    }

    patchExistingData(): void {
        // Map old calculation type values to new ones
        let calculationType = this.existingData.calculationType || LayerExposedConstants.CALCULATION_TYPE.BASIC;
        if (calculationType === LayerExposedConstants.CALCULATION_TYPE.LAYER_EXPOSED) {
            calculationType = LayerExposedConstants.CALCULATION_TYPE.BASIC;
        } else if (calculationType === LayerExposedConstants.CALCULATION_TYPE.LAYER_EXPOSED_WITH_DEDUCTIBLE) {
            calculationType = LayerExposedConstants.CALCULATION_TYPE.WITH_DEDUCTIBLE;
        }
        
        // Ensure arrays are initialized
        if (!this.locationTIVFields) {
            this.locationTIVFields = [];
        }
        if (!this.locationPolicyIdFields) {
            this.locationPolicyIdFields = [];
        }
        if (!this.locationClientFields) {
            this.locationClientFields = [];
        }
        if (!this.policyDatasetAllFields) {
            this.policyDatasetAllFields = [];
        }
        
        // If old form has baseValue or tivValue, ensure it's in the TIV dropdown
        const tivValueId = this.existingData.tivValue || this.existingData.baseValue;
        if (tivValueId && this.locationDataset) {
            const tivField = this.locationDataset?.fields.find(f => f.id === tivValueId);
            if (tivField) {
                const isInTIVDropdown = this.locationTIVFields.some(f => f.id === tivValueId);
                if (!isInTIVDropdown) {
                    this.locationTIVFields = [...this.locationTIVFields, tivField];
                }
            }
        }
        
        
        // If old form has limitValue (old name for layerValue), ensure it's in the policy dropdown
        if (this.existingData.limitValue && this.policyDataset) {
            const limitValueField = this.policyDataset?.fields.find(f => f.id === this.existingData.limitValue);
            if (limitValueField) {
                const isInPolicyDropdown = this.policyDatasetAllFields.some(f => f.id === this.existingData.limitValue);
                if (!isInPolicyDropdown) {
                    this.policyDatasetAllFields = [...this.policyDatasetAllFields, limitValueField];
                }
            }
        }
        
        // If old form has policyIdLocation, ensure it's in the policy dropdown
        if (this.existingData.policyIdLocation && this.policyDataset) {
            const policyIdLocationField = this.policyDataset?.fields.find(f => f.id === this.existingData.policyIdLocation);
            if (policyIdLocationField) {
                const isInPolicyDropdown = this.policyDatasetAllFields.some(f => f.id === this.existingData.policyIdLocation);
                if (!isInPolicyDropdown) {
                    this.policyDatasetAllFields = [...this.policyDatasetAllFields, policyIdLocationField];
                }
            }
        }
        
        this.createAccountForm.patchValue({
            calculationType: calculationType,
            name: decodeHtmlText(this.existingData.name),
            // New field names with fallback to old field names for backward compatibility
            policyIdPolicy: this.existingData.policyIdPolicy || this.existingData.policyId || this.existingData.policyIdLocation || '',
            client: this.existingData.client || this.existingData.clientId || '',
            tivValue: this.existingData.tivValue || this.existingData.baseValue || '',
            deductible: this.existingData.deductible || '',
            layerValue: this.existingData.layerValue || this.existingData.limitValue || '',
            minimumDeductible: this.existingData.minimumDeductible || '',
            maximumDeductible: this.existingData.maximumDeductible || '',
            attachmentPoint: this.existingData.attachmentPoint || this.existingData.attachmentPointValue || '',
            participation: this.existingData.participation || this.existingData.participationValue || '',
        });
    }

    getData() {
        this.accountService.findAccount(this.accountId).subscribe((account) => {
            const locationDataset = account.datasets.find((dataset: any) => dataset.name === LayerExposedConstants.DATASET_NAME.LOCATIONS );
            if (locationDataset) {
                this.locationDataset = locationDataset;
                this.locationDatasetDropdown = locationDataset.fields.filter(
                    (field) =>
                        field.type === LayerExposedConstants.FIELD_TYPE.NUMBER_FIELD &&
                        field.scope === DatasetFieldScope.INTERNAL
                );
                // Show all numeric fields from Location dataset for TIV Value selection
                this.locationTIVFields = locationDataset.fields.filter(
                    (field) =>
                        field.type === LayerExposedConstants.FIELD_TYPE.NUMBER_FIELD &&
                        field.scope === DatasetFieldScope.INTERNAL
                );
                // Filter TEXT fields for Policy ID selection (includes Contract ID, etc.)
                this.locationPolicyIdFields = locationDataset.fields.filter(
                    (field) => (field.type === LayerExposedConstants.FIELD_TYPE.SHORT_TEXT_FIELD || field.type === LayerExposedConstants.FIELD_TYPE.LONG_TEXT_FIELD) && field.scope === DatasetFieldScope.INTERNAL
                );
                
                // Filter TEXT fields for Group by Client selection
                this.locationClientFields = locationDataset.fields.filter(
                    (field) => (field.type === LayerExposedConstants.FIELD_TYPE.SHORT_TEXT_FIELD || field.type === LayerExposedConstants.FIELD_TYPE.LONG_TEXT_FIELD) && field.scope === DatasetFieldScope.INTERNAL
                );
            }
            const policyDataset = account.datasets.find((dataset: any) => dataset.name === LayerExposedConstants.DATASET_NAME.POLICIES);
            if (policyDataset) {
                this.policyDataset = policyDataset; // Store reference for patchExistingData
                this.policyDatasetDropdown = policyDataset.fields.filter(field => field.type === LayerExposedConstants.FIELD_TYPE.NUMBER_FIELD);
                this.policyDatasetAllFields = policyDataset.fields; // All fields for Policy ID selection
                
                // Show all numeric fields from Policy dataset for Minimum Deductible
                this.policyMinimumDeductibleFields = policyDataset.fields.filter(
                    (field) => field.type === LayerExposedConstants.FIELD_TYPE.NUMBER_FIELD
                );
                
                // Show all numeric fields from Policy dataset for Maximum Deductible
                this.policyMaximumDeductibleFields = policyDataset.fields.filter(
                    (field) => field.type === LayerExposedConstants.FIELD_TYPE.NUMBER_FIELD
                );
                
                // Filter SHORT_TEXT fields for Policy ID breakdown field
                this.policyIdFields = policyDataset.fields.filter(
                    (field) => field.type === LayerExposedConstants.FIELD_TYPE.SHORT_TEXT_FIELD
                );
            }
            
            // Patch existing data after datasets are loaded
            if (this.existingData) {
                this.patchExistingData();
            }
        });
    }
    
    initForm(): void {
        this.isFormDisabled = false;
        this.createAccountForm = this.fb.group({
            calculationType: [LayerExposedConstants.CALCULATION_TYPE.BASIC, [Validators.required]], // Default to BASIC
            name: ['', [Validators.required]],
            policyIdPolicy: ['', [Validators.required]], // Always required for both BASIC and WITH_DEDUCTIBLE
            client: [''], // Optional
            tivValue: ['', [Validators.required]],
            deductible: [''], // Optional by default
            layerValue: ['', [Validators.required]],
            minimumDeductible: [''], // Optional by default
            maximumDeductible: [''], // Optional by default
            attachmentPoint: ['', [Validators.required]],
            participation: ['', [Validators.required]],
        });
        this.createAccountForm.updateValueAndValidity();
    }

    selectCalculationType(type: string): void {
        this.createAccountForm.get('calculationType')?.setValue(type);
        
        // Update validators based on calculation type
        const deductibleControl = this.createAccountForm.get('deductible');
        const minimumDeductibleControl = this.createAccountForm.get('minimumDeductible');
        const maximumDeductibleControl = this.createAccountForm.get('maximumDeductible');
        
        if (type !== LayerExposedConstants.CALCULATION_TYPE.WITH_DEDUCTIBLE) {
            // Clear deductible fields when switching away from WITH_DEDUCTIBLE
            deductibleControl?.setValue('');
            minimumDeductibleControl?.setValue('');
            maximumDeductibleControl?.setValue('');
        }
        
        // All deductible fields are optional
        deductibleControl?.clearValidators();
        minimumDeductibleControl?.clearValidators();
        maximumDeductibleControl?.clearValidators();
        
        // Update validity
        deductibleControl?.updateValueAndValidity();
        minimumDeductibleControl?.updateValueAndValidity();
        maximumDeductibleControl?.updateValueAndValidity();
    }

    clearFormValidators(formGroup: UntypedFormGroup | FormGroup): void {
        Object.keys(formGroup.controls).forEach((key) => {
          const control = formGroup.get(key);
          if (control instanceof UntypedFormGroup || control instanceof FormGroup) {
            this.clearFormValidators(control);
          } else if (control instanceof UntypedFormControl) {
            control.clearValidators();
            control.updateValueAndValidity();
          } else {
            control.clearValidators();
            control.updateValueAndValidity();
          }
        });
      }

      
    onCancel() {
        this.onCancelClose.emit();
        this.changeDetector.detectChanges();
    }
    submitLayerExpose(): void {
        this.createAccountForm.markAllAsTouched();
        if (this.createAccountForm.valid) {

            const formData = this.createAccountForm.value;
            formData.name = formData.name?.trim();
            if (!formData.name) {
                this.createAccountForm.controls['name'].setErrors({ required: true });
                this.createAccountForm.controls['name'].markAsTouched();    
                return;
            }
            if (this.existingData && this.isEdit) {
                const updatePayload = {
                    id: this.existingData.id,
                    accountId: this.accountId,
                    calculationType: formData.calculationType,
                    name: formData.name,
                    policyIdPolicy: formData.policyIdPolicy,
                    policyIdLocation: formData.policyIdPolicy, // Backward compatibility
                    tivValue: formData.tivValue,
                    layerValue: formData.layerValue,
                    attachmentPoint: formData.attachmentPoint,
                    participation: formData.participation,
                    isApplied: this.existingData.isApplied,
                    // For WITH_DEDUCTIBLE, include deductible fields (empty string if not filled)
                    ...(formData.calculationType === LayerExposedConstants.CALCULATION_TYPE.WITH_DEDUCTIBLE ? {
                        deductible: formData.deductible || '',
                        minimumDeductible: (formData.minimumDeductible && formData.minimumDeductible !== 'NONE') ? formData.minimumDeductible : '',
                        maximumDeductible: (formData.maximumDeductible && formData.maximumDeductible !== 'NONE') ? formData.maximumDeductible : ''
                    } : {}),
                    // Optional fields
                    ...(formData.client && { client: formData.client })
                };

                try {
                    let apiError: any;
                    this.layerExposeService.updateLayerExpose(this.accountId, this.existingData.id, updatePayload)
                        .pipe(
                            catchError((error) => {
                                apiError = error;
                                return of(null);
                            })
                        )
                        .subscribe((data) => {
                            if (apiError?.error?.errors) {
                                this.notifService.error(apiError.error.errors);
                            } else {
                                this.clearFormValidators(this.createAccountForm);
                                this.createAccountForm.reset();
                                this.notifService.success(
                                    LayerExposedConstants.SUCCESS_MESSAGE.UPDATED
                                );
                                this.updatedSuccessfully.emit();
                                this.changeDetector.detectChanges();
                            }

                        });
                } catch (error) {
                    console.error('Layer Exposed update error:', error);
                    this.notifService.error('An unexpected error occurred while updating');
                }
            }
            else if (!this.isEdit) {
                const myPayload = {
                    accountId: this.accountId,
                    calculationType: formData.calculationType,
                    name: formData.name,
                    policyIdPolicy: formData.policyIdPolicy,
                    policyIdLocation: formData.policyIdPolicy, // Backward compatibility
                    tivValue: formData.tivValue,
                    layerValue: formData.layerValue,
                    attachmentPoint: formData.attachmentPoint,
                    participation: formData.participation,
                    isApplied: true,
                    // For WITH_DEDUCTIBLE, include deductible fields (empty string if not filled)
                    ...(formData.calculationType === LayerExposedConstants.CALCULATION_TYPE.WITH_DEDUCTIBLE ? {
                        deductible: formData.deductible || '',
                        minimumDeductible: (formData.minimumDeductible && formData.minimumDeductible !== 'NONE') ? formData.minimumDeductible : '',
                        maximumDeductible: (formData.maximumDeductible && formData.maximumDeductible !== 'NONE') ? formData.maximumDeductible : ''
                    } : {}),
                    // Optional fields
                    ...(formData.client && { client: formData.client })
                };
                try {
                    let apiError: any;
                    this.layerExposeService.createLayerExpose(this.accountId, myPayload)
                        .pipe(
                            catchError((error) => {
                                apiError = error;
                                return of(null);
                            })
                        )
                        .subscribe((data) => {
                            if (apiError?.error?.errors) {
                                this.notifService.error(apiError.error.errors);
                            } else {
                                this.clearFormValidators(this.createAccountForm);
                                this.createAccountForm.reset();
                                this.notifService.success(
                                    LayerExposedConstants.SUCCESS_MESSAGE.CREATED
                                );
                                this.updatedSuccessfully.emit();
                                this.changeDetector.detectChanges();
                            }
                        });
                } catch (error) {
                    console.error('Layer Exposed create error:', error);
                    this.notifService.error('An unexpected error occurred while creating');
                }
            }

        }
    }

    removeSpaces() {
        const nameControl = this.createAccountForm.get('name');
        if (nameControl) {
          nameControl.setValue(nameControl.value.replace(/^\s+/, ''), { emitEvent: false });
        }
    }
}
